import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import altezaLogo from "@/assets/alteza-logo.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email: value });
      toast.success("You're on the list — welcome to Alteza.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-foreground text-background py-20 lg:py-28 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img src={altezaLogo} alt="Alteza Marketplace" className="h-12 lg:h-14 w-auto object-contain brightness-0 invert" />
          <p className="mt-5 text-sm text-background/70 max-w-md leading-relaxed">
            A curated marketplace for handmade candles, skincare, bath &amp; body, beauty, and jewelry —
            from female-owned brands you'll actually love supporting.
          </p>
          <div className="mt-6 flex flex-col gap-2 text-sm text-background/70">
            <a href="https://instagram.com/Altezamarketplace" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-primary transition-colors">
              <Instagram className="w-4 h-4" />
              <span>@Altezamarketplace</span>
            </a>
            <a href="https://tiktok.com/@Altezamarketplace" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-primary transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.32 6.69a5.65 5.65 0 0 1-3.43-1.16 5.66 5.66 0 0 1-2.16-3.53h-3.4v13.78a2.7 2.7 0 1 1-2.7-2.7c.28 0 .55.05.81.13V9.65a6.1 6.1 0 0 0-.81-.05 6.16 6.16 0 1 0 6.16 6.16V8.94a8.99 8.99 0 0 0 5.53 1.88V7.42a5.65 5.65 0 0 1-.0-.73z" />
              </svg>
              <span>@Altezamarketplace</span>
            </a>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs tracking-[0.2em] uppercase text-background/50 mb-4 font-semibold">Shop</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/shop?category=Candles" className="hover:text-primary transition-colors">Candles</Link></li>
            <li><Link to="/shop?category=Skincare" className="hover:text-primary transition-colors">Skincare</Link></li>
            <li><Link to="/shop?category=Bath %26 Body" className="hover:text-primary transition-colors">Bath &amp; Body</Link></li>
            <li><Link to="/shop?category=Beauty" className="hover:text-primary transition-colors">Beauty</Link></li>
            <li><Link to="/shop?category=Jewelry" className="hover:text-primary transition-colors">Jewelry</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs tracking-[0.2em] uppercase text-background/50 mb-4 font-semibold">Sellers</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/sell" className="hover:text-primary transition-colors">Open your shop</Link></li>
            <li><Link to="/register?role=seller" className="hover:text-primary transition-colors">Apply</Link></li>
            <li><Link to="/sell#benefits" className="hover:text-primary transition-colors">Seller benefits</Link></li>
            <li><Link to="/resources" data-testid="footer-seller-resources" className="hover:text-primary transition-colors">Seller Resources</Link></li>
            <li><Link to="/seller-agreement" data-testid="footer-seller-agreement" className="hover:text-primary transition-colors">Seller Agreement</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase text-background/50 mb-4 font-semibold">The Letter</div>
          <p className="text-sm text-background/70 mb-4">New makers, new collections — once a month.</p>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex border-b border-background/30">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent outline-none flex-1 py-2 text-sm placeholder:text-background/40"
              placeholder="you@email.com"
            />
            <button
              data-testid="newsletter-submit"
              disabled={submitting}
              className="text-xs uppercase tracking-wider text-primary font-semibold disabled:opacity-60"
              type="submit"
            >
              {submitting ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 pt-8 border-t border-background/10 text-xs text-background/40 flex flex-wrap gap-4 justify-between items-center">
        <span>© {new Date().getFullYear()} Alteza Marketplace</span>
        <Link to="/help" data-testid="footer-help" className="hover:text-primary transition-colors">Help Centre</Link>
        <Link to="/contact" data-testid="footer-contact" className="hover:text-primary transition-colors">Contact</Link>
        <span>Canadian-based · Worldwide reach</span>
      </div>
    </footer>
  );
}
